/**
 * `InputBase` with a native `<input>` prop surface, for call sites that drive
 * the field like a plain DOM input: event-based `onChange`, arbitrary `type`
 * values (`date`, `time`, `number`), `id` for an external `<label htmlFor>`.
 *
 * `InputBase` spreads its rest props onto the underlying input element, so all
 * of that works at runtime — but its declared props inherit React Aria
 * TextField's value-based `onChange: (value: string) => void`, which is wrong
 * for standalone use and makes every event handler an unsafe `any`. The single
 * cast below carries the honest DOM typing for every call site instead of a
 * per-site assertion. (base/input is vendored and must not be edited.)
 */

import { InputBase } from '@/components/base/input/input';

import type { ComponentPropsWithoutRef, Ref } from 'react';

type InputBaseProps = ComponentPropsWithoutRef<typeof InputBase>;

interface NativeInputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'size' | 'className'> {
  /** Visual size of the field box. */
  readonly size?: 'sm' | 'md';
  /**
   * Chrome-level disabled styling. Defaults to the native `disabled` prop, so
   * passing `disabled` alone dims the box AND disables the input.
   */
  readonly isDisabled?: boolean;
  /** Class for the input element (text, padding). */
  readonly inputClassName?: string;
  /** Class for the group wrapper (box size, margins, background). */
  readonly wrapperClassName?: string;
  readonly ref?: Ref<HTMLInputElement>;
}

export function NativeInput({ disabled, isDisabled, ...rest }: NativeInputProps) {
  const domProps = { ...rest, disabled } as unknown as InputBaseProps;
  return <InputBase {...domProps} isDisabled={isDisabled ?? disabled} />;
}
